import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAssessmentContent } from "../../contexts/assessmentContentContext";
import { useAvailableAssessmentBenchmarks } from "../../hooks/useAvailableAssessmentBenchmarks";
import { Link as RouterLink, useLocation } from "react-router-dom";

type TestSummary = {
  pk: string;
  sk: string;
  entity_type: string;
  grade: string;
  test_type: string;
  status?: string | null;
  benchmark?: string | null;
  updated_at?: string | null;
  question_count: number;
};

const formatDate = (value?: string | null): string => {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const sortByGrade = (left: TestSummary, right: TestSummary): number =>
  left.grade.localeCompare(right.grade);

export function AssessmentsManagerPage() {
  const { search } = useLocation();
  const { listAssessmentTests } = useAssessmentContent();
  const { benchmarks } = useAvailableAssessmentBenchmarks();
  const [tests, setTests] = useState<TestSummary[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const selectedBenchmark = useMemo(
    () =>
      new URLSearchParams(search).get("benchmark")?.trim().toLowerCase() || "",
    [search],
  );
  const isDemo = useMemo(
    () =>
      new URLSearchParams(search).get("is_demo")?.trim().toLowerCase() || "",
    [search],
  );
  const [demoSkill, setDemoSkill] = useState<string | null>(null);
  useEffect(() => {
    if (selectedBenchmark.includes("reading-demo")) {
      setDemoSkill("reading");
    } else if (selectedBenchmark.includes("listening-demo")) {
      setDemoSkill("listening");
    }
  }, [selectedBenchmark]);

  useEffect(() => {
    const controller = new AbortController();

    const loadTests = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const [readingTestsResponse, listeningTestsResponse] =
          await Promise.all([
            listAssessmentTests({
              testType: "reading",
              benchmark: selectedBenchmark,
              signal: controller.signal,
            }),
            listAssessmentTests({
              testType: "listening",
              benchmark: selectedBenchmark,
              signal: controller.signal,
            }),
          ]);

        const dedupedTests = new Map<string, TestSummary>();
        for (const test of [
          ...readingTestsResponse,
          ...listeningTestsResponse,
        ]) {
          dedupedTests.set(`${test.pk}-${test.sk}`, test);
        }
        setTests(Array.from(dedupedTests.values()));
      } catch (requestError: unknown) {
        if (
          requestError instanceof DOMException &&
          requestError.name === "AbortError"
        ) {
          return;
        }
        setError(
          requestError instanceof Error
            ? requestError.message
            : "Unable to load tests.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadTests().then();

    return () => {
      controller.abort();
    };
  }, [listAssessmentTests, selectedBenchmark]);

  const benchmarkFilteredTests = useMemo(() => {
    if (!selectedBenchmark) {
      return tests;
    }
    return tests.filter(
      (item) =>
        (item.benchmark ?? "").trim().toLowerCase() === selectedBenchmark,
    );
  }, [selectedBenchmark, tests]);

  const readingTests = useMemo(
    () =>
      benchmarkFilteredTests
        .filter((item) => item.test_type === "reading")
        .sort(sortByGrade),
    [benchmarkFilteredTests],
  );
  const listeningTests = useMemo(
    () =>
      benchmarkFilteredTests
        .filter((item) => item.test_type === "listening")
        .sort(sortByGrade),
    [benchmarkFilteredTests],
  );
  const selectedBenchmarkSummary = useMemo(
    () =>
      benchmarks.find(
        (version) => version.id.trim().toLowerCase() === selectedBenchmark,
      ) ?? null,
    [selectedBenchmark, benchmarks],
  );

  const selectedBenchmarkSkill = selectedBenchmarkSummary?.skill ?? null;
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Link
        component={RouterLink}
        to="/assessments-admin/benchmarks"
        underline="hover"
        sx={{ display: "inline-block", mb: 1.5, fontSize: 14 }}
      >
        Back to dashboard
      </Link>
      <Typography variant="h4" component="h1" gutterBottom>
        Existing Assessment Tests
      </Typography>
      {selectedBenchmark ? (
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack>
            {selectedBenchmarkSummary ? (
              <Typography variant="body2" color="text.secondary">
                Benchmark: <strong>{selectedBenchmarkSummary.title}</strong>
              </Typography>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              ID: {selectedBenchmark}
            </Typography>
          </Stack>
          <Button
            component={RouterLink}
            to={`/assessments-admin/benchmarks/${selectedBenchmark}/edit`}
            variant="outlined"
            size="small"
            sx={{ height: "fit-content" }}
          >
            Edit Benchmark
          </Button>
        </Stack>
      ) : null}
      {isLoading ? (
        <Typography variant="body1" sx={{ mb: 2 }}>
          Loading tests...
        </Typography>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {!isLoading && !error ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {selectedBenchmarkSkill == "reading" || demoSkill == "reading" ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="h2">
                  Reading Tests ({readingTests.length})
                </Typography>
                <Button
                  component={RouterLink}
                  to={`/assessments-admin/reading-builder${selectedBenchmark ? `?benchmark=${encodeURIComponent(selectedBenchmark)}` : ""}${isDemo ? `&is_demo=${encodeURIComponent(isDemo)}` : ""}`}
                  variant="outlined"
                  size="small"
                >
                  Add a Test
                </Button>
              </Box>
              <TestTable tests={readingTests} isDemo={isDemo} />
            </Paper>
          ) : null}
          {selectedBenchmarkSkill == "listening" || demoSkill == "listening" ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="h2">
                  Listening Tests ({listeningTests.length})
                </Typography>
                <Button
                  component={RouterLink}
                  to={`/assessments-admin/listening-builder${selectedBenchmark ? `?benchmark=${encodeURIComponent(selectedBenchmark)}` : ""}${isDemo ? `&is_demo=${encodeURIComponent(isDemo)}` : ""}`}
                  variant="outlined"
                  size="small"
                >
                  Add a Test
                </Button>
              </Box>
              <TestTable tests={listeningTests} isDemo={isDemo} />
            </Paper>
          ) : null}
        </Box>
      ) : null}
    </Container>
  );
}

function TestTable({
  tests,
  isDemo,
}: {
  tests: TestSummary[];
  isDemo?: string;
}) {
  if (tests.length === 0) {
    return <Typography color="text.secondary">No tests found.</Typography>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Difficulty Level</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Benchmark ID</TableCell>
            <TableCell>Questions</TableCell>
            <TableCell>Updated At</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tests.map((test) => (
            <TableRow key={`${test.pk}-${test.sk}`}>
              <TableCell>{test.grade || "-"}</TableCell>
              <TableCell>{test.status || "-"}</TableCell>
              <TableCell>{test.benchmark || "-"}</TableCell>
              <TableCell>{test.question_count}</TableCell>
              <TableCell>{formatDate(test.updated_at)}</TableCell>
              <TableCell align="right">
                <Button
                  component={RouterLink}
                  to={`/assessments-admin/${test.test_type}-builder?pk=${encodeURIComponent(test.pk)}${test.benchmark ? `&benchmark=${encodeURIComponent(test.benchmark)}` : ""}${isDemo ? `&is_demo=${encodeURIComponent(isDemo)}` : ""}`}
                  variant="text"
                  size="small"
                >
                  Open Editor
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
